import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { SIGNUP_USER_JOBS } from '../../../../../../shared/constants/signupConstants';
import { snakeToCamel } from '../../../../caseUtils';
import { getErrorMessage } from '../../../../utils/errorMessage';
import { signup } from '../../../signup.api';
import { withSignUp } from '../../SignUpContext';
import { checkIndividualPosition, linkedInTrack } from '../signUpUserUtils';
import { SignUpError } from './SignUpError';
import { SignUpFormEmail } from './SignUpFormEmail';
import { SignUpFormName } from './SignUpFormName';
import { SignUpFormPassword } from './SignUpFormPassword';
import { SignUpFormStructure } from './SignUpFormStructure';
import { SignUpFormTerms } from './SignUpFormTerms';
import { SignUpLinkToSignIn } from './SignUpLinkToSignIn';
import { SignUpTitle } from './SignUpTitle';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import { UserJob } from '../../../signup.types';

type Props = RouteComponentProps & {
  saveEmail: Function;
  source: string;
  sourcePage: string;
  saveIsICP: Function;
};

type State = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  jobTitle: string;
  position: string;
  firmSize: string;
  agreedToTerms: boolean;
  isSubmitting: boolean;
  errorMessage: string;
  errorType: string;
};

const USER_JOBS: Array<UserJob> = snakeToCamel(SIGNUP_USER_JOBS);

export class RawSignUpForm extends React.Component<Props, State> {
  state: State = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    jobTitle: 'initial',
    position: 'initial',
    firmSize: 'initial',
    agreedToTerms: false,
    isSubmitting: false,
    errorMessage: '',
    errorType: '',
  };

  handleJobTitleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      jobTitle: event.target.value,
      firmSize: 'initial',
      position: 'initial',
      errorMessage: '',
      errorType: '',
    });
  };

  handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    // @ts-ignore ✅
    this.setState({
      [name]: value,
      errorMessage: '',
      errorType: '',
    });
  };

  handleTerms = () => {
    this.setState(({ agreedToTerms }) => ({
      agreedToTerms: !agreedToTerms,
      errorMessage: '',
      errorType: '',
    }));
  };

  handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const {
      firstName,
      lastName,
      email,
      password,
      jobTitle,
      position,
      firmSize,
      agreedToTerms,
    } = this.state;
    const signupInfos = {
      firstName,
      lastName,
      email,
      password,
      jobTitle: jobTitle === 'initial' ? '' : jobTitle,
      position: checkIndividualPosition({ position, jobTitle, firmSize }),
      firmSize: firmSize === 'initial' ? '' : firmSize,
      agreedToTerms,
    };
    this.setState({ isSubmitting: true });
    try {
      const isFirm = (await signup(signupInfos)).firm;
      this.props.saveEmail(this.state.email);
      doctrineAnalytics('Account SignUpFormValidated', {
        source: this.props.source,
        sourcePage: this.props.sourcePage,
      });
      const userJob = USER_JOBS.find(element => element.jobTitle === jobTitle);
      if (userJob && userJob.isIcp) {
        if (window.fbq) window.fbq('track', 'CompleteRegistration');
        doctrineAnalytics('Account ICPSignUpFormValidated', {
          source: this.props.source,
          sourcePage: this.props.sourcePage,
        });
        linkedInTrack();
        this.props.saveIsICP();
      }
      this.setState({ isSubmitting: false });
      const nextStepUrl = `/inscription${
        isFirm ? '/mail' : '/authentification'
      }${window.location.search}`;
      this.props.history.push(nextStepUrl);
      window.scrollTo(0, 0);
    } catch (error) {
      if (error.response && error.response.data) {
        const errorType = error.response.data.error;
        doctrineAnalytics('Account SignUpFormError', {
          error: errorType,
          source: this.props.source,
          sourcePage: this.props.sourcePage,
        });
        this.setState({
          isSubmitting: false,
          errorType,
          errorMessage: getErrorMessage(error.response),
        });
      }
    }
  };

  render() {
    return (
      <>
        <SignUpTitle />
        <SignUpLinkToSignIn className="signup-form__signin__top" />
        <form onSubmit={this.handleSubmit}>
          <SignUpFormName
            firstName={this.state.firstName}
            lastName={this.state.lastName}
            handleChange={this.handleChange}
          />
          <SignUpFormEmail
            email={this.state.email}
            handleChange={this.handleChange}
          />
          <SignUpFormPassword
            password={this.state.password}
            handleChange={this.handleChange}
          />
          <SignUpFormStructure
            jobTitle={this.state.jobTitle}
            position={this.state.position}
            firmSize={this.state.firmSize}
            handleChange={this.handleChange}
            handleJobTitleChange={this.handleJobTitleChange}
            USER_JOBS={USER_JOBS}
          />
          <SignUpFormTerms
            handleTerms={this.handleTerms}
            agreedToTerms={this.state.agreedToTerms}
            jobTitle={this.state.jobTitle}
            USER_JOBS={USER_JOBS}
          />
          {this.state.errorMessage && (
            <SignUpError
              errorMessage={this.state.errorMessage}
              errorType={this.state.errorType}
            />
          )}
          <button
            type="submit"
            className="button brand large expanded"
            name="submit-form"
            data-test-id="submit-form"
            disabled={this.state.isSubmitting}
          >
            <FormattedMessage id="signup/routes/user/form/SignUpForm.tsx/1" />
          </button>
        </form>
        <SignUpLinkToSignIn className="hide_on--tablet" />
      </>
    );
  }
}

export const SignUpForm = withSignUp(withRouter(RawSignUpForm));
